import { useEffect, useRef } from 'react';
import { isBrowser } from 'react-device-detect';
import { on, off, DEVICES_REGEXP } from 'utils';

export function getClosestBody(el) {
  if (!el) {
    return null;
  } else if (el.tagName === 'BODY') {
    return el;
  } else if (el.tagName === 'IFRAME') {
    const document = el.contentDocument;
    return document ? document.body : null;
  } else if (!el.offsetParent) {
    return null;
  }

  return getClosestBody(el.offsetParent);
}

function preventDefault(rawEvent) {
  const e = rawEvent || window.event;
  // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
  if (e.touches.length > 1) return true;

  if (e.preventDefault) e.preventDefault();

  return false;
}

const isIosDevice =
  isBrowser &&
  window.navigator &&
  window.navigator.platform &&
  DEVICES_REGEXP.ios.test(window.navigator.platform);

const bodies = new Map();

const doc = typeof document === 'object' ? document : undefined;

let documentListenerAdded = false;

const useLockBody = !doc
  ? function useLockBodyMock(_locked = true, _elementRef) {}
  : function useLockBody(locked = true, elementRef) {
      const bodyRef = useRef(doc.body);
      elementRef = elementRef || bodyRef;

      const lock = body => {
        const bodyInfo = bodies.get(body);
        if (!bodyInfo) {
          bodies.set(body, { counter: 1, initialOverflow: body.style.overflow });
          if (isIosDevice) {
            if (!documentListenerAdded) {
              on(document, 'touchmove', preventDefault, { passive: false });
              documentListenerAdded = true;
            }
          } else {
            const scrollWidth = window.innerWidth - document.body.offsetWidth;

            body.style.paddingRight = `${scrollWidth}px`;
            body.style.overflow = 'hidden';
          }
        } else {
          bodies.set(body, {
            counter: bodyInfo.counter + 1,
            initialOverflow: bodyInfo.initialOverflow,
          });
        }
      };

      const unlock = body => {
        const bodyInfo = bodies.get(body);
        if (bodyInfo) {
          if (bodyInfo.counter === 1) {
            bodies.delete(body);
            if (isIosDevice) {
              body.ontouchmove = null;

              if (documentListenerAdded) {
                off(document, 'touchmove', preventDefault);
                documentListenerAdded = false;
              }
            } else {
              body.style.paddingRight = '0px';
              body.style.overflow = bodyInfo.initialOverflow;
            }
          } else {
            bodies.set(body, {
              counter: bodyInfo.counter - 1,
              initialOverflow: bodyInfo.initialOverflow,
            });
          }
        }
      };

      useEffect(() => {
        const body = getClosestBody(elementRef.current);
        if (!body) {
          return;
        }
        if (locked) {
          lock(body);
        } else {
          unlock(body);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [locked, elementRef.current]);

      // clean up, on un-mount
      useEffect(() => {
        const body = getClosestBody(elementRef.current);
        if (!body) {
          return;
        }
        return () => {
          unlock(body);
        };
      }, [elementRef]);
    };

export default useLockBody;
